import React from "react";
import { MessageCircle, Phone, MessageSquareText, Mail } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { links } from "../../lib/api";
import { Container } from "../site/Reveal";

export const WhatsAppLogo = ({ size = 34 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M16.004 3C8.82 3 3 8.82 3 16c0 2.29.6 4.53 1.74 6.5L3 29l6.68-1.7A12.95 12.95 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16.004 3Zm0 23.6c-2.03 0-4.02-.55-5.75-1.6l-.41-.24-3.96 1.01 1.06-3.86-.27-.4A10.56 10.56 0 0 1 5.4 16c0-5.85 4.76-10.6 10.6-10.6 5.85 0 10.6 4.75 10.6 10.6 0 5.85-4.75 10.6-10.6 10.6Zm5.82-7.94c-.32-.16-1.88-.93-2.17-1.03-.29-.11-.5-.16-.71.16-.21.32-.82 1.03-1 1.24-.18.21-.37.24-.69.08-.32-.16-1.34-.5-2.55-1.58-.94-.84-1.58-1.88-1.76-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.71-.97-2.34-.26-.62-.52-.53-.71-.54h-.61c-.21 0-.56.08-.85.4-.29.32-1.11 1.09-1.11 2.65 0 1.56 1.14 3.07 1.3 3.28.16.21 2.24 3.42 5.43 4.8.76.33 1.35.52 1.81.67.76.24 1.45.21 2 .13.61-.09 1.88-.77 2.14-1.51.27-.74.27-1.38.19-1.51-.08-.13-.29-.21-.61-.37Z" />
  </svg>
);

const Big = ({ href, onClick, icon, label, sub, cls, testid }) => {
  const Tag = href ? "a" : "button";
  return (
    <Tag href={href} onClick={onClick} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
      className={`group flex items-center gap-4 rounded-2xl px-5 py-5 text-left text-white shadow-[0_16px_34px_-16px_rgba(0,0,0,.45)] transition-transform duration-200 hover:-translate-y-1 ${cls}`} data-testid={testid}>
      <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 ring-2 ring-white/40 transition-transform duration-200 group-hover:scale-110">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[22px] font-extrabold leading-tight md:text-[24px]">{label}</span>
        <span className="block truncate text-[15px] font-semibold text-white/90">{sub}</span>
      </span>
    </Tag>
  );
};

export default function ContactStrip() {
  const { content, openChat, t } = useSite();
  const l = links(content);
  return (
    <section className="py-10 md:py-14" data-testid="contact-strip">
      <Container>
        <div className="rounded-3xl border border-[#d6efe9] bg-[#f0faf7] p-5 md:p-8">
          <h2 className="font-jakarta text-[26px] font-extrabold text-[#0f2e2a] md:text-[32px]">{t("Talk to a real person now")}</h2>
          <p className="mt-1 text-[16px] font-medium text-[#3d504d]">{t("Chat, call, text or WhatsApp — we reply fast with a free quote.")}</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Big onClick={() => openChat()} icon={<MessageCircle size={34} strokeWidth={2.6} />} label={t("Live Chat")} sub={t("Reply in minutes")} cls="bg-[#0e8c7f] hover:bg-[#0b7569]" testid="contact-strip-chat" />
            <Big href={l.tel} icon={<Phone size={34} strokeWidth={2.6} />} label={t("Call Us")} sub={content?.phone_display} cls="bg-[#0f2e2a] hover:bg-[#173f3a]" testid="contact-strip-call" />
            <Big href={l.sms} icon={<MessageSquareText size={34} strokeWidth={2.6} />} label={t("Text Us")} sub={content?.phone_display} cls="bg-[#2563eb] hover:bg-[#1d4fd8]" testid="contact-strip-text" />
            <Big href={l.wa} icon={<WhatsAppLogo size={38} />} label="WhatsApp" sub={content?.whatsapp_display} cls="bg-[#25D366] hover:bg-[#1ebe5a]" testid="contact-strip-whatsapp" />
          </div>
          <div className={`mt-4 grid gap-3 ${content?.email2 ? "md:grid-cols-2" : ""}`}>
            {[[content?.email, l.mail, "contact-strip-email"], [content?.email2, l.mail2, "contact-strip-email2"]].filter(([e]) => e).map(([e, href, id]) => (
              <a key={id} href={href} className="flex flex-wrap items-center justify-center gap-x-2 rounded-2xl border-2 border-[#0e8c7f] bg-white px-5 py-4 text-[16px] font-bold text-[#0f2e2a] transition-colors hover:bg-[#e6f8f3] md:text-[18px]" data-testid={id}>
                <Mail size={22} className="shrink-0 text-[#0e8c7f]" /><span>{t("Email us")}:</span><span className="break-all text-[#0b6f65]">{e}</span>
              </a>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
